import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { marked } from "marked";

interface RowDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  rowData: any;
}

const RowDetailsDialog: React.FC<RowDetailsDialogProps> = ({
  open,
  onClose,
  rowData,
}) => {
  // Helper function to format numbers with commas
  const formatNumber = (value: any): string => {
    if (!value || value === "Not Available") return "Not Available";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "Not Available";
    return num.toLocaleString("en-US");
  };
  console.log(rowData);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle variant="h6" sx={{ color: "#1976d2" }}>
        {rowData?.["Order Number"] || "N/A"}
      </DialogTitle>
      <DialogContent>
        <Box>
          {rowData && (
            <>
              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                }}
              >
                <CardHeader
                  title="Agent Summary"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      rowGap: 0.2,
                    }}
                  >
                    {/* Row 1, Col 1 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Recommendation
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Action"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 1, Col 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Justification
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Justification"] || "N/A"}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title="Customer & Operational Summary"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      rowGap: 0.2,
                    }}
                  >
                    {/* Row 1 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Address
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Customer Address"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Home Value
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Home Value"] &&
                        rowData["Home Value"] !== "Not Available"
                          ? `$${formatNumber(rowData["Home Value"])}`
                          : "Not Available"}
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        District Name
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["District"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Planning Area Name
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Planning Area"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Repair Time (mins)
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["RepairTime (Mins)"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Trips
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Trips"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 4 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Response Time
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Response Time"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Cycle Time
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Cycle Time"] || "N/A"}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title="Quoted Summary"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      rowGap: 0.2,
                    }}
                  >
                    {/* Row 1 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        JobCode Quoted
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["JobCode Quoted"] || "N/A"}
                      </Box>
                    </Box>
                    <Box></Box>

                    {/* Row 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Total Quoted
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Total Quoted"]
                          ? `$${rowData["Total Quoted"]}`
                          : "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Total Labor
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Labor Quoted"]
                          ? `$${rowData["Labor Quoted"]}`
                          : "N/A"}
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Total Parts
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Parts Quoted"]
                          ? `$${rowData["Parts Quoted"]}`
                          : "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Parts Markdown
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Parts Markdown"]
                          ? `$${rowData["Parts Markdown"]}`
                          : "N/A"}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title="Repair Summary"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}
                >
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        minWidth: "200px",
                        color: "#666",
                        fontSize: "0.875rem",
                      }}
                    >
                      Decline Reason:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        color: "#333",
                        wordBreak: "break-word",
                        fontSize: "0.875rem",
                      }}
                    >
                      {rowData["Decline Reason"]}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        minWidth: "200px",
                        color: "#666",
                        fontSize: "0.875rem",
                      }}
                    >
                      Service Request:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        color: "#333",
                        wordBreak: "break-word",
                        fontSize: "0.875rem",
                      }}
                    >
                      {rowData["Service Request"]}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        minWidth: "200px",
                        color: "#666",
                        fontSize: "0.875rem",
                      }}
                    >
                      Tech Comments:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        color: "#333",
                        wordBreak: "break-word",
                        fontSize: "0.875rem",
                      }}
                    >
                      {rowData["Tech Comments"]}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        minWidth: "200px",
                        color: "#666",
                        fontSize: "0.875rem",
                      }}
                    >
                      Repair Higher Than MSRP:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        color: "#333",
                        wordBreak: "break-word",
                        fontSize: "0.875rem",
                      }}
                    >
                      {rowData["Repair > MSRP"]}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginTop: 2,
                  fontSize: "12px",
                }}
              >
                <CardHeader
                  title="Appliance Details"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      rowGap: 0.2,
                    }}
                  >
                    {/* Row 1 - Appliance Summary (full width, no 40-60 split) */}
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Box
                        component="span"
                        sx={{ color: "#333", fontSize: "0.875rem" }}
                      >
                        {rowData["Appliance Summary"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Appliance Type
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Appliance Type"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Brand
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Brand"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Merchandise Code
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Merchandise Code"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Model Number
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Model Number"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 4 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Purchase Date
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Purchase Date"] || "N/A"}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "40%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Serial Number
                      </Box>
                      <Box
                        sx={{
                          width: "60%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Serial Number"] || "N/A"}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title="Appliance Recommendation"
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    "& ol": { margin: "0" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: "#333", fontSize: "0.875rem" }}
                  >
                    {renderList(rowData["Appliance Recommendations#1"] || "")}
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#333",
                        }}
                      >
                        Create Call Summary
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          color: "#666",
                          fontStyle: "italic",
                        }}
                      >
                        *Data is available for Sears Kairos Scheduler Calls
                        only.
                      </Typography>
                    </Box>
                  }
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}
                  >
                    {/* Row 1 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Create Source
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Create Source"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Upsell Attempt
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Upsell Attempt"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        HashCode URL
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["HashCode URL"] ? (
                          <a
                            href={rowData["HashCode URL"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "underline",
                            }}
                          >
                            {rowData["HashCode URL"]}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </Box>
                    </Box>

                    {/* Row 4 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Audio Url
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Audio URL"] ? (
                          <a
                            href={rowData["Audio URL"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "underline",
                            }}
                          >
                            {rowData["Audio URL"]}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </Box>
                    </Box>

                    {/* Row 5 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Agent Chat Classification
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["LLM Chat Classification"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 6 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Agent Chat Summary:
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: marked.parse(
                            rowData["LLM Chat Summary"] || "",
                            { async: false }
                          ) as string,
                        }}
                      ></Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "12px",
                  marginTop: 2,
                }}
              >
                <CardHeader
                  title={
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#333",
                        }}
                      >
                        Feedback and Ratings
                      </Typography>
                    </Box>
                  }
                  sx={{
                    bgcolor: "#e9ecef",
                    borderBottom: "1px solid #e0e0e0",
                    py: 0.5,
                    "& .MuiCardHeader-title": {
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#333",
                    },
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}
                  >
                    {/* Row 1 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Customer Rating
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Customer Rating"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Customer Feedback
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Customer Feedback"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Tech Rating
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Tech Rating"] || "N/A"}
                      </Box>
                    </Box>

                    {/* Row 4 */}
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: "20%",
                          fontWeight: 600,
                          color: "#666",
                          fontSize: "0.875rem",
                        }}
                      >
                        Tech Feedback
                      </Box>
                      <Box
                        sx={{
                          width: "80%",
                          color: "#333",
                          fontSize: "0.875rem",
                        }}
                      >
                        {rowData["Tech Feedback"] || "N/A"}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const linkifyText = (text: string): string => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s\)]+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${url}</a>`;
  });
};

const renderList = (
  value: string,
  listType: "ol" | "ul" = "ol",
  delimiter: string = "\n"
) => {
  if (!value) return null;

  const ListTag = listType;

  return (
    <ListTag style={{ margin: 0, paddingLeft: "1rem" }}>
      {value.split(delimiter).map((item: string, index: number) => {
        let cleanedItem = item;
        if (listType === "ol") {
          cleanedItem = cleanedItem.replace(/^\d+\.\s*/, "");
        }
        return (
          <li key={index} style={{ marginBottom: "0.35rem" }}>
            <span
              dangerouslySetInnerHTML={{ __html: linkifyText(cleanedItem) }}
            />
          </li>
        );
      })}
    </ListTag>
  );
};

export default RowDetailsDialog;
